import { useCallback, useEffect, useRef, useState } from 'react';
import {
  listNotifications,
  markNotificationRead,
  markNotificationPopupSeen,
  markNotificationsReadBulk,
  unreadCount,
  archiveNotification,
  unarchiveNotification,
  deleteNotification,
  getNotificationPreferences,
  saveNotificationPreference,
  replaceNotificationPreferences,
} from './notificationsApi';

export const useNotificationsInbox = (firebase, options = {}) => {
  const {
    status = 'all',
    limit = 20,
    pollIntervalMs = 20000,
  } = options;

  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [backendUnavailable, setBackendUnavailable] = useState(false);
  const loadingRef = useRef(false);
  const timerRef = useRef(null);

  const load = useCallback(async (cursor, runOptions = {}) => {
    const force = Boolean(runOptions?.force);
    if (!firebase) return;
    if (backendUnavailable && !force) return;
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    try {
      const data = await listNotifications(firebase, { status, limit, cursor });
      if (data?.unavailable) {
        if (!cursor) {
          setItems([]);
          setNextCursor(null);
        }
        setError(null);
        setBackendUnavailable(true);
        return;
      }
      if (data?.notModified) {
        setError(null);
        return;
      }
      if (cursor) {
        setItems((prev) => [...prev, ...(data.items || [])]);
      } else {
        setItems(data.items || []);
      }
      setNextCursor(data.nextCursor || null);
      setError(null);
      setBackendUnavailable(false);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [firebase, status, limit, backendUnavailable]);

  useEffect(() => {
    load(null);
  }, [load]);

  useEffect(() => {
    if (!pollIntervalMs || backendUnavailable) return undefined;
    timerRef.current = setInterval(() => load(null), pollIntervalMs);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [load, pollIntervalMs, backendUnavailable]);

  const refresh = useCallback(() => load(null, { force: true }), [load]);
  const fetchNext = useCallback(() => {
    if (!nextCursor) return;
    load(nextCursor);
  }, [load, nextCursor]);

  const markRead = useCallback(async (id) => {
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, readAt: Date.now() } : item)));
    try {
      await markNotificationRead(firebase, id);
    } catch (_err) {
      // ignore
    }
  }, [firebase]);

  const markPopupSeen = useCallback(async (id) => {
    const now = Date.now();
    setItems((prev) => prev.map((item) => (
      item.id === id
        ? { ...item, popupShownAt: item.popupShownAt || now, popupEligible: false }
        : item
    )));
    try {
      await markNotificationPopupSeen(firebase, id);
    } catch (_err) {
      // ignore
    }
  }, [firebase]);

  const markAllRead = useCallback(async () => {
    setItems((prev) => prev.map((item) => ({ ...item, readAt: item.readAt || Date.now() })));
    try {
      await markNotificationsReadBulk(firebase);
    } catch (_err) {
      // ignore
    }
  }, [firebase]);

  const archive = useCallback(async (id) => {
    setItems((prev) => {
      if (status === 'active') {
        return prev.filter((item) => item.id !== id);
      }
      return prev.map((item) => (item.id === id ? { ...item, archivedAt: Date.now() } : item));
    });
    try {
      await archiveNotification(firebase, id);
    } catch (_err) {
      // ignore
    }
  }, [firebase, status]);

  const unarchive = useCallback(async (id) => {
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, archivedAt: null } : item)));
    try {
      await unarchiveNotification(firebase, id);
    } catch (_err) {
      // ignore
    }
  }, [firebase]);

  const remove = useCallback(async (id) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
    try {
      await deleteNotification(firebase, id);
    } catch (_err) {
      // ignore
    }
  }, [firebase]);

  return {
    items,
    nextCursor,
    loading,
    error,
    refresh,
    fetchNext,
    markRead,
    markPopupSeen,
    markAllRead,
    archive,
    unarchive,
    remove,
  };
};

export const useUnreadCount = (firebase, options = {}) => {
  const { pollIntervalMs = 20000 } = options;
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [backendUnavailable, setBackendUnavailable] = useState(false);
  const timerRef = useRef(null);

  const load = useCallback(async (runOptions = {}) => {
    const force = Boolean(runOptions?.force);
    if (!firebase) return;
    if (backendUnavailable && !force) return;
    setLoading(true);
    try {
      const data = await unreadCount(firebase);
      if (data?.unavailable) {
        setCount(0);
        setError(null);
        setBackendUnavailable(true);
        return;
      }
      if (data?.notModified) {
        setError(null);
        return;
      }
      setCount(data.count || 0);
      setError(null);
      setBackendUnavailable(false);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [firebase, backendUnavailable]);

  useEffect(() => {
    load();
    if (!pollIntervalMs || backendUnavailable) return undefined;
    timerRef.current = setInterval(load, pollIntervalMs);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [load, pollIntervalMs, backendUnavailable]);

  return {
    count,
    loading,
    error,
    refresh: () => load({ force: true }),
  };
};

export const useNotificationPreferences = (firebase) => {
  const [preferences, setPreferences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!firebase) return;
    setLoading(true);
    try {
      const data = await getNotificationPreferences(firebase);
      setPreferences(data.preferences || []);
      setError(null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [firebase]);

  useEffect(() => {
    load();
  }, [load]);

  const savePreference = useCallback(async (preference) => {
    if (!firebase) return [];
    try {
      const data = await saveNotificationPreference(firebase, preference);
      const next = data.preferences || [];
      setPreferences(next);
      return next;
    } catch (err) {
      setError(err);
      return preferences;
    }
  }, [firebase, preferences]);

  const replacePreferences = useCallback(async (nextPreferences) => {
    if (!firebase) return [];
    try {
      const data = await replaceNotificationPreferences(firebase, nextPreferences);
      const next = data.preferences || [];
      setPreferences(next);
      return next;
    } catch (err) {
      setError(err);
      return preferences;
    }
  }, [firebase, preferences]);

  return {
    preferences,
    loading,
    error,
    refresh: load,
    savePreference,
    replacePreferences,
  };
};
